// Left-hand panels of the AI site editor: the section list, the theme and
// the site settings.
import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Eye, EyeOff, GripVertical, PanelBottom, PanelTop, Plus } from "lucide-react";
import { COLOR_LABELS, FONTS, sectionsOf, withSections } from "./siteModel";

const Row = ({ label, selected, hidden, onSelect, onToggle, dragHandle, icon }) => (
  <div className={`group flex items-center gap-1.5 rounded-lg border px-2 py-2 text-sm transition ${selected ? "border-accent bg-accent/5" : "border-transparent hover:bg-gray-100"}`}>
    {dragHandle || <span className="flex w-4 justify-center text-gray-400">{icon}</span>}
    <button type="button" onClick={onSelect} className={`min-w-0 flex-1 truncate text-left ${hidden ? "text-gray-400 line-through" : "text-slate-800"} ${selected ? "font-semibold" : ""}`}>
      {label}
    </button>
    {onToggle && (
      <button type="button" onClick={onToggle} aria-label={hidden ? `Show ${label}` : `Hide ${label}`} title={hidden ? "Hidden on your website" : "Shown on your website"}
        className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-slate-700">
        {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    )}
  </div>
);

export const SectionsList = ({ site, selected, onSelect, onChange, onAdd }) => {
  const sections = sectionsOf(site);

  const onDragEnd = ({ source, destination }) => {
    if (!destination || source.index === destination.index) return;
    const next = [...sections];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    onChange(withSections(site, next));
  };
  const toggle = (id) => onChange(withSections(site, sections.map((s) => (s.id === id ? { ...s, hidden: !s.hidden } : s))));

  return (
    <div className="flex flex-col gap-1">
      <Row label="Header" icon={<PanelTop size={14} />} selected={selected === "header"} onSelect={() => onSelect("header")} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="ai-sections">
          {(drop) => (
            <div ref={drop.innerRef} {...drop.droppableProps} className="flex flex-col gap-1">
              {sections.map((s, index) => (
                <Draggable key={s.id} draggableId={s.id} index={index}>
                  {(drag) => (
                    <div ref={drag.innerRef} {...drag.draggableProps}>
                      <Row
                        label={s.name}
                        selected={selected === s.id}
                        hidden={s.hidden}
                        onSelect={() => onSelect(s.id)}
                        onToggle={() => toggle(s.id)}
                        dragHandle={<span {...drag.dragHandleProps} aria-label={`Drag to move ${s.name}`} className="cursor-grab text-gray-400"><GripVertical size={16} /></span>}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {drop.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Row label="Footer" icon={<PanelBottom size={14} />} selected={selected === "footer"} onSelect={() => onSelect("footer")} />
      <button type="button" onClick={onAdd}
        className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:border-accent hover:text-accent">
        <Plus size={16} /> Add section
      </button>
      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        Click any text on the page to edit it. To use your own photo, click it or its <strong>Change photo</strong> button. Events, news and prayer times update on their own.
      </p>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
    {children}
  </label>
);

const inputClass = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

export const ThemePanel = ({ site, onChange }) => {
  const theme = site.theme || { colors: {}, fonts: {} };
  const setColor = (key, value) =>
    onChange({ ...site, theme: { ...theme, colors: { ...theme.colors, [key]: value } } }, `color-${key}`);
  const setFont = (key, value) => onChange({ ...site, theme: { ...theme, fonts: { ...theme.fonts, [key]: value } } });
  const fontOptions = (current) => (FONTS.includes(current) ? FONTS : [current, ...FONTS].filter(Boolean));

  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Colours</h3>
        <div className="space-y-2">
          {COLOR_LABELS.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3 text-sm text-slate-700">
              {label}
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-500">{theme.colors?.[key]}</span>
                <input type="color" aria-label={label} value={theme.colors?.[key] || "#000000"} onChange={(e) => setColor(key, e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-gray-300 bg-white p-0.5" />
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-800">Fonts</h3>
        <Field label="Headings">
          <select className={inputClass} value={theme.fonts?.heading || ""} onChange={(e) => setFont("heading", e.target.value)}>
            {fontOptions(theme.fonts?.heading).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Text">
          <select className={inputClass} value={theme.fonts?.body || ""} onChange={(e) => setFont("body", e.target.value)}>
            {fontOptions(theme.fonts?.body).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
};

export const SettingsPanel = ({ site, onChange }) => {
  const seo = site.seo || {};
  const setSeo = (key, value) => onChange({ ...site, seo: { ...seo, [key]: value } }, `seo-${key}`);
  const [showDirection, setShowDirection] = useState(false);
  return (
    <div className="space-y-4">
      <Field label="Title in Google and browser tabs">
        <input className={inputClass} maxLength={70} value={seo.title || ""} onChange={(e) => setSeo("title", e.target.value)} placeholder="Your masjid's name" />
      </Field>
      <Field label="Description in Google results">
        <textarea className={inputClass} rows={3} maxLength={160} value={seo.description || ""} onChange={(e) => setSeo("description", e.target.value)} />
        <span className="mt-1 block text-right text-xs text-gray-400">{(seo.description || "").length}/160</span>
      </Field>
      <Field label="Language">
        <select className={inputClass} value={site.language || "en"} onChange={(e) => onChange({ ...site, language: e.target.value })}>
          <option value="en">English</option>
          <option value="ar">Arabic (right to left)</option>
        </select>
      </Field>
      <button type="button" onClick={() => setShowDirection((v) => !v)} className="text-xs font-medium text-accent hover:underline">
        {showDirection ? "Hide" : "Show"} the AI's design notes
      </button>
      {showDirection && (
        <Field label="Design notes (the AI follows these when it edits or adds sections)">
          <textarea className={inputClass} rows={5} maxLength={800} value={site.designDirection || ""}
            onChange={(e) => onChange({ ...site, designDirection: e.target.value }, "direction")} />
        </Field>
      )}
    </div>
  );
};
